import { useTranslation } from 'react-i18next';
import { LevelMeterBar, LevelMeterTrack } from './LevelMeter.styles';

const METER_HEIGHT_PX = 32;
const MIN_BAR_HEIGHT_PX = 2;
const LOW_SIGNAL_LEVEL = 0.02;
const CLEAR_SIGNAL_LEVEL = 0.2;

// A fixed waveform silhouette, scaled by the level; a random one would flicker between renders.
const shape = (bars: number) =>
  Array.from(
    { length: bars },
    (_, index) => 0.2 + 0.8 * Math.abs(Math.sin(index * 1.7) * Math.cos(index * 0.6))
  );

const describeLevel = (level: number) => {
  if (level < LOW_SIGNAL_LEVEL) return 'none';
  if (level < CLEAR_SIGNAL_LEVEL) return 'low';
  return 'signal';
};

export const LevelMeter = ({
  level,
  label,
  bars = 40,
  className = 'w-32',
}: {
  level: number;
  /** Two meters on one screen need different names. */
  label: string;
  bars?: number;
  className?: string;
}) => {
  const { t } = useTranslation();
  const clampedLevel = Math.max(0, Math.min(1, level));
  return (
    <LevelMeterTrack
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clampedLevel * 100)}
      aria-valuetext={t(`audio.settings.levelValue.${describeLevel(clampedLevel)}`)}
      aria-live="off"
      className={className}
    >
      {shape(bars).map((share, index) => (
        <LevelMeterBar
          key={index}
          style={{
            height: `${Math.max(MIN_BAR_HEIGHT_PX, Math.round(share * clampedLevel * METER_HEIGHT_PX))}px`,
          }}
        />
      ))}
    </LevelMeterTrack>
  );
};
